/**
 * SSE（Server-Sent Events）連線
 *
 * 後端 WebSocket 服務已於 commit 4e8d7dc50 整組移除，前端各處改成定時輪詢。
 * 這支 hook 把即時推播接回來 —— 但用 SSE 而不是 WebSocket：
 * 這些場景的資料流本來就是單向的（送出都走 REST），而 EventSource 是瀏覽器原生、
 * 會自動重連、且重連時自動帶 `Last-Event-ID`，斷線期間漏掉的事件由後端補齊。
 *
 * ⚠️ 這支 hook 只負責「連線與偵活」，不負責決定收到事件要做什麼。
 *    呼叫端把既有的輪詢函式接在 onEvent 上即可 —— 於是輪詢那條路可以原封不動留著當降級，
 *    而且兩條路走的是同一段判斷邏輯，不會出現「推播版與輪詢版行為不一致」。
 */
export const useSse = () => {

    /**
     * 超過這段時間沒收到「任何」事件就判定假死。
     *
     * ⚠️ 後端的心跳刻意做成具名的 `ping` 事件而不是 SSE 註解行（`: hb`）：
     *    註解行足以讓中間的代理知道連線還活著，但它在 JS 層完全看不見，
     *    前端就無法分辨「安靜」與「TCP 還開著但資料早就不流動」。
     *    後端心跳是 20 秒，這裡抓 3 倍餘裕。
     */
    const DEAD_AFTER_MS = 60 * 1000

    let source = null
    let watchdog = null
    let closed = false

    /**
     * 開始連線
     *
     * @param url     SSE 端點
     * @param events  要監聽的事件名（後端的 command 字串）
     * @param onEvent 收到任一事件時呼叫（通常直接接既有的輪詢函式）
     * @param onDead  判定連線不可用時呼叫（呼叫端應在此啟動降級輪詢）
     * @return 是否成功建立（false 表示瀏覽器不支援，呼叫端應直接走輪詢）
     */
    const startSse = (
        url,
        events,
        onEvent,
        onDead) => {

        if (typeof window === `undefined` || !window.EventSource) return false

        //⚠️⚠️ 一定要接上 VITE_BASE_URL ⚠️⚠️
        //EventSource 沒有 axios 那層 baseURL, 相對路徑是對「頁面來源」解析的。
        //本機開發時頁面在 localhost:4092（Vite）而 API 在別的網域,
        //而且 vite.config.ts 沒有設 proxy —— 相對路徑會打到 Vite 上, 必然 404。
        //正式區因為頁面與 API 同網域, 相對路徑剛好會通, 所以這個錯只在本機炸出來。
        const base = (import.meta.env.VITE_BASE_URL) || ``
        const target = /^https?:\/\//.test(url)
            ? url
            : `${base.replace(/\/$/, ``)}/${url.replace(/^\//, ``)}`

        closed = false

        const dead = () => {

            if (closed) return

            closed = true

            stopSse()

            onDead()

        }

        const touch = () => {

            if (watchdog !== null) clearTimeout(watchdog)

            watchdog = setTimeout(dead, DEAD_AFTER_MS)

        }

        source = new EventSource(target)

        source.onopen = () => {
            touch()
        }

        source.onerror = () => {

            //readyState 仍在 CONNECTING 表示原生重連機制正在運作，交給它，不要插手
            if (source?.readyState === EventSource.CONNECTING) return

            //⚠️ readyState 是 CLOSED 代表這是「終局錯誤」——
            //   依規範, 回應不是 200 或 Content-Type 不對時, 瀏覽器會關閉連線且「不再重連」。
            //   原本這裡要累積 MAX_FAILURE 次才降級, 但這種錯永遠只會發生一次,
            //   於是降級沒被觸發, 只能等 60 秒的 watchdog —— 使用者看到的就是
            //   「掃碼成功了但網頁卡著不動, 過了快一分鐘才突然進去」。
            dead()

        }

        //ready / ping 也要收下來重置 watchdog，它們就是用來證明連線還活著的
        const listen = [...events, `ready`, `ping`]

        listen.forEach(name => {

            source?.addEventListener(name, () => {

                touch()

                //ping 純粹是心跳，不該觸發呼叫端的動作
                if (name === `ping`) return

                onEvent()

            })

        })

        touch()

        return true

    }

    /**
     * 關閉連線
     *
     * ⚠️ 呼叫端務必在「權杖一次性領取」這類流程裡先呼叫它再往下走，
     *    語意等同原本的 stopPolling()。
     */
    const stopSse = () => {

        closed = true

        if (watchdog !== null) clearTimeout(watchdog)

        watchdog = null

        //已經斷掉的連線再 close 會拋，這裡沒有補救動作
        try {
            source?.close()
        } catch (e) {
            //ignore
        }

        source = null

    }

    return {
        startSse,
        stopSse
    }

}
